import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Paper,
  Stack,
  Link,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { calculateCompoundInterest } from './compoundUtils';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);


const CompoundInterestApp = () => {
  const [initial, setInitial] = useState(1000);
  const [monthly, setMonthly] = useState(200);
  const [rate, setRate] = useState(5); // annual interest rate
  const [years, setYears] = useState(40);

  const [chartData, setChartData] = useState(null);
  const [summary, setSummary] = useState({
    withInterest: 0,
    withoutInterest: 0,
    contributions: 0,
  });

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(value || 0);

  useEffect(() => {
    const { chartData: nextChartData, summary: nextSummary } = calculateCompoundInterest({
      initial,
      monthly,
      rate,
      years,
    });
    setChartData(nextChartData);
    setSummary(nextSummary);
  }, [initial, monthly, rate, years]);

  const inputFields = [
    {
      label: 'Initial deposit',
      value: initial,
      onChange: (e) => setInitial(e.target.value),
      adornment: <InputAdornment position="start">£</InputAdornment>,
      min: 0,
      step: 100,
    },
    {
      label: 'Monthly add',
      value: monthly,
      onChange: (e) => setMonthly(e.target.value),
      adornment: <InputAdornment position="start">£</InputAdornment>,
      min: 0,
      step: 50,
    },
    {
      label: 'Rate (%/yr)',
      value: rate,
      onChange: (e) => setRate(e.target.value),
      adornment: <InputAdornment position="end">%</InputAdornment>,
      min: 0,
      step: 0.1,
    },
    {
      label: 'Term (years)',
      value: years,
      onChange: (e) => setYears(e.target.value),
      adornment: <InputAdornment position="end">yrs</InputAdornment>,
      min: 1,
      step: 1,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Compound Interest Calculator | Talk About Finance</title>
        <meta
          name="description"
          content="Project how regular deposits and different rates change your savings growth with a compound interest calculator and chart."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/compound-interest-calculator" />
      </Helmet>

      <Container sx={{ mt: 6, mb: 8, pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.shadows[1],
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
              : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
        }}
      >
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <Box>
            <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
              Back to apps
            </Button>
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Calculator
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Compound Interest
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '100%' }}>
            See how contributions and time can accelerate your savings. Adjust the inputs to compare growth with and without compounding.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Want the why behind the numbers?{' '}
            <Link
              component={RouterLink}
              to="/articles/compound-interest-friend-or-foe"
              underline="none"
              color="primary"
              sx={{
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(25, 118, 210, 0.12)'
                    : 'rgba(144, 202, 249, 0.16)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.18)'
                      : 'rgba(144, 202, 249, 0.24)',
                },
              }}
            >
              Read the guide
            </Link>
          </Typography>
        </Stack>
      </Paper>

      <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {inputFields.map((item) => (
            <Box
              key={item.label}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                boxShadow: (theme) => theme.shadows[0],
                minWidth: 150,
                maxWidth: 220,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.3 }}>
                {item.label}
              </Typography>
              <TextField
                value={item.value}
                onChange={item.onChange}
                size="small"
                type="number"
                fullWidth
                InputProps={{
                  inputProps: { min: item.min, step: item.step },
                  startAdornment: item.adornment?.props.position === 'start' ? item.adornment : undefined,
                  endAdornment: item.adornment?.props.position === 'end' ? item.adornment : undefined,
                }}
                sx={{ mt: 0.5 }}
              />
            </Box>
            ))}
          </Box>

        {chartData && (
          <Box mt={4}>
            <Line data={chartData} />
          </Box>
        )}

        <Paper
            variant="outlined"
            sx={{
              mt: 3,
              borderRadius: 2,
              overflow: 'hidden',
            borderColor: 'divider',
          }}
          >
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Without compounding (stuffed under the mattress)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatCurrency(summary.withoutInterest)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Extra earned from compounding</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(summary.withInterest - summary.withoutInterest)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Final balance with compounding</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatCurrency(summary.withInterest)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            We use the UK AER convention: the annual rate is treated as AER, converted to an equivalent monthly compound rate,
            interest is compounded monthly, and monthly contributions are applied at the end of each month over the selected number of years.
          </Typography>
      </Paper>
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          About these calculations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These figures are illustrative and based on the assumptions shown. They may not reflect the terms of specific financial products or your individual circumstances. You should verify results independently and, where appropriate, seek advice from a qualified professional.
        </Typography>
      </Box>
      </Container>
    </>
  );
};

export default CompoundInterestApp;
